import { destroyStore, setStoreCpiInventoryEnabled, useLegalEntities } from '@gozoki/api'
import type { Store } from '@gozoki/api-types'
import { FLAGS } from '@gozoki/feature-flags'
import { getCountryLabel } from '@gozoki/tools'
import { Box, FormControlLabel, Switch, TextField, Tooltip, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { z } from 'zod'
import LoadingPaper from '~/components/containers/LoadingPaper'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { ConfirmResourceDeletionButton } from '~/components/communication/ConfirmDeletionButton'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import { StoreTagChips } from '~/components/routes/stores/StoreTagChips'
import { STORE_TAB_PERMISSIONS } from '~/components/routes/stores/tabs'
import { useStore } from '~/components/routes/stores/useStore'
import { FeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import { fonts } from '~/utils/theme/fonts.theme'
import { storeStatusLabel } from '~/utils/types/toFrench'

export const Route = createFileRoute('/_authenticated/stores/$storeId/details')({
  staticData: {
    permission: STORE_TAB_PERMISSIONS.details,
  },
  component: StoreInformationsTab,
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

/**
 * Toggle « Inventaire RFID CPI » (colonne `cpi_inventory_enabled` de la boutique).
 * Rendu uniquement si le flag Flipt `cpi-inventory-enabled-toggle` est actif (voir appelant).
 * Désactivé (avec tooltip) pour un admin sans droit d'édition boutique ; le backend refait
 * la garde RBAC de son côté.
 */
function CpiInventoryToggle({ store, storeId }: { store: Store | undefined; storeId: number }) {
  const queryClient = useQueryClient()
  const guard = usePermissionGuard(['edit', 'stores.store-list.store'])
  const enabled = store?.cpiInventoryEnabled ?? false

  const mutation = useMutation({
    mutationFn: setStoreCpiInventoryEnabled,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.stores.show.queryOptions({ params: { id: storeId } }).queryKey,
      })
    },
  })

  const control = (
    <Switch
      checked={enabled}
      disabled={!store || !guard.authorized || mutation.isPending}
      onChange={(event) => mutation.mutate({ storeId, enabled: event.target.checked })}
    />
  )

  return (
    <>
      <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
        Inventaire RFID CPI
      </Typography>
      <FormControlLabel
        control={
          guard.authorized ? (
            control
          ) : (
            <Tooltip title={guard.tooltip}>
              <span>{control}</span>
            </Tooltip>
          )
        }
        label={enabled ? 'Activé' : 'Désactivé'}
      />
    </>
  )
}

function StoreInformationsTab() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useParams()

  const { store, isLoading } = useStore(storeId)
  const { data: legalEntities = [] } = useLegalEntities()

  const currentEntity =
    legalEntities?.find((entity) => entity.id === store?.legalEntityId)?.name ??
    store?.legalEntityId

  const deleteStoreMut = useMutation({
    mutationFn: destroyStore,
    onSuccess: () => {
      navigate({ to: '/stores' })
    },
  })

  return (
    <StoreDetailsLayout
      storeId={storeId}
      tab="details"
      headerActions={
        <>
          <ConfirmResourceDeletionButton
            permission={['delete', 'stores.store-list.store']}
            handleResourceDeletion={() => deleteStoreMut.mutateAsync(storeId)}
            disabled={deleteStoreMut.isPending}
          />
          <AuthorizedButton
            permission={['edit', 'stores.store-list.store']}
            variant="contained"
            onClick={() => navigate({ to: '/stores/new', search: { storeId } })}
          >
            Modifier
          </AuthorizedButton>
        </>
      }
    >
      <Box sx={{ margin: 'auto', width: '50vw' }}>
        <LoadingPaper sx={{ padding: '24px', paddingBottom: '40px' }} loading={isLoading}>
          <Typography mb="8px" style={fonts.inputHeader}>
            Référence
          </Typography>
          <TextField disabled fullWidth value={store?.checkpointStoreRef} />
          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Nom
          </Typography>
          <TextField disabled fullWidth value={store?.label} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Nom court
          </Typography>
          <TextField disabled fullWidth value={store?.shortLabel ?? ''} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Adresse
          </Typography>
          <TextField disabled fullWidth value={store?.address?.address ?? ''} />

          {store?.address?.addressAdditionnal && (
            <>
              <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
                Adresse additionnelle
              </Typography>
              <TextField disabled fullWidth value={store.address.addressAdditionnal} />
            </>
          )}

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Ville
          </Typography>
          <TextField disabled fullWidth value={store?.address?.city ?? ''} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Code postal
          </Typography>
          <TextField disabled fullWidth value={store?.address?.postalCode ?? ''} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Pays
          </Typography>
          <TextField
            disabled
            fullWidth
            value={store?.address?.country ? getCountryLabel(store.address.country) : ''}
          />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Référence Luna
          </Typography>
          <TextField disabled fullWidth value={store?.lunaStoreRef} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Référence Bearcod
          </Typography>
          <TextField disabled fullWidth value={store?.bearcodName} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Type Boutique
          </Typography>
          <TextField disabled fullWidth value={store?.storeType} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Entreprise Légal
          </Typography>
          <TextField disabled fullWidth value={currentEntity ?? 'Inconnue'} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            SIRET
          </Typography>
          <TextField disabled fullWidth value={store?.siret} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Code analytique
          </Typography>
          <TextField disabled fullWidth value={store?.analyticCode ?? ''} />

          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Status
          </Typography>
          <TextField
            disabled
            fullWidth
            value={store?.status ? storeStatusLabel[store.status] : '—'}
          />
          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Date d'ouverture
          </Typography>
          <TextField
            disabled
            fullWidth
            value={
              store?.openingDate ? DateTime.fromISO(store?.openingDate).toLocaleString() : '  --  '
            }
          />
          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Facteur de quantité (pour l'ouverture)
          </Typography>
          <TextField disabled fullWidth value={store?.quantityFactor} />
          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Taux de croissance
          </Typography>
          <TextField disabled fullWidth value={store?.growthFactor} />
          <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
            Tags
          </Typography>
          {store?.tags?.length ? (
            <StoreTagChips tags={store.tags} />
          ) : (
            <Typography color="text.secondary">Aucun tag</Typography>
          )}

          <FeatureFlag flagKey={FLAGS.cpiInventoryEnabledToggle}>
            <CpiInventoryToggle store={store} storeId={storeId} />
          </FeatureFlag>
        </LoadingPaper>
      </Box>
    </StoreDetailsLayout>
  )
}
